import { createTRPCClient, httpLink, loggerLink } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import { observable } from '@trpc/server/observable';
import superjson from 'superjson';
import { QueryClient } from '@tanstack/react-query';
import { type Router } from '@shelflix/trpc/routes';
import { TRPCLink } from '@trpc/client';
import axios from 'axios';

export const queryClient = new QueryClient();

const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:3000') + '/trpc';

let isRefreshing = false;
let pendingRequests: (() => void)[] = [];

async function refreshToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  try {
    const res = await axios.post(
      API_URL + '/authentication.refreshToken',
      { json: { refreshToken } },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!res.status) throw new Error('Refresh token failed');

    const data = await res.data;

    const newAccessToken = data.result.data?.json?.accessToken;

    if (newAccessToken) {
      localStorage.setItem('authToken', newAccessToken);
    }

    return newAccessToken;
  } catch (err) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    throw err;
  }
}

const authLink = (): TRPCLink<Router> => {
  return () => {
    return ({ op, next }) => {
      return observable((observer) => {
        const executeRequest = () => {
          const subscription = next(op).subscribe({
            next(result) {
              observer.next(result);
            },
            error: async (err) => {
              if (err.message === 'Access token inválido' || err.message === 'Token inválido') {
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
                window.location.href = '/login';
                return;
              }

              const isUnauthorized = err.message === 'jwt expired' || err.message === 'jwt token revoked';

              if (!isUnauthorized) {
                observer.error(err);
                return;
              }

              if (!isRefreshing) {
                isRefreshing = true;
                try {
                  await refreshToken();
                  pendingRequests.forEach((cb) => cb());
                  pendingRequests = [];
                } catch (e) {
                  pendingRequests = [];
                  observer.error(e);
                  window.location.href = '/login';
                  return;
                } finally {
                  isRefreshing = false;
                }
              } else {
                await new Promise<void>((resolve) => pendingRequests.push(() => resolve()));
              }

              const retrySub = next(op).subscribe({
                next(result) {
                  observer.next(result);
                },
                error(error) {
                  observer.error(error);
                },
                complete() {
                  observer.complete();
                },
              });

              return () => {
                retrySub.unsubscribe();
              };
            },
            complete() {
              observer.complete();
            },
          });

          return () => {
            subscription.unsubscribe();
          };
        };

        return executeRequest();
      });
    };
  };
};

const trpcClient = createTRPCClient<Router>({
  links: [
    loggerLink(),
    authLink(),
    httpLink({
      url: API_URL,
      async headers() {
        const token = localStorage.getItem('authToken');
        // Domain-based tenant resolution: send current hostname for tenant detection
        const tenantDomain = window.location.hostname;

        const headers: Record<string, string> = {};

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Send the current domain for domain-based tenant resolution
        // The backend will resolve the tenant from this domain
        if (tenantDomain && tenantDomain !== 'localhost' && tenantDomain !== '127.0.0.1') {
          headers['x-tenant-domain'] = tenantDomain;
        }

        return headers;
      },
      transformer: superjson,
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<Router>({
  client: trpcClient,
  queryClient,
});

export { trpcClient };
